import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import { CustomCollapse } from './../../../../CustomCollapse/CustomCollapse';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from './../../../../PrevNextButtons';

import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';

export default function Representation() {
  const navigate = useNavigate();
  const { nextRoute, previousRoute } = useTaskNavigation();
  const { application } = useSelector((state: ApplicationState) => state.apply);

  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/representation');

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  const { initial, current } = getApplicationData(application);
  const { representative } = current;
  const { representative: initialRepresentative } = initial;

  const showHelpedByAgent = representative?.isHelpedByAgent || initialRepresentative?.isHelpedByAgent;
  const showHelpedByFamilyMember =
    representative?.isHelpedByFamilyMember || initialRepresentative?.isHelpedByFamilyMember;
  const showHelpedByFriend = representative?.isHelpedByFriend || initialRepresentative?.isHelpedByFriend;
  const showHelpedBySchoolCounsellor =
    representative?.isHelpedBySchoolCounsellor || initialRepresentative?.isHelpedBySchoolCounsellor;
  const showHelpedByTeacher = representative?.isHelpedByTeacher || initialRepresentative?.isHelpedByTeacher;
  const showHelpedByStudent = representative?.isHelpedByStudent || initialRepresentative?.isHelpedByStudent;
  const showHelpedByOther = representative?.isHelpedByOther || initialRepresentative?.isHelpedByOther;

  const notHelpedByAnyone = !(
    showHelpedByAgent ||
    showHelpedByFamilyMember ||
    showHelpedByFriend ||
    showHelpedBySchoolCounsellor ||
    showHelpedByTeacher ||
    showHelpedByStudent ||
    showHelpedByOther
  );

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.REPRESENTATION} />

        {notHelpedByAnyone ? (
          <Box bgColor="utility.100" px={5} py={3} color="muted">
            The applicant has no data for this task
          </Box>
        ) : (
          <>
            <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
              Data
            </CustomHeading>

            <Box>
              {showHelpedByAgent && (
                <CustomCollapse defaultIsOpen>
                  <CustomCollapse.Trigger>{`Agent/agency representation`}</CustomCollapse.Trigger>
                  <CustomCollapse.Content>
                    <Field
                      leftLabel="Agency name"
                      value={representative?.agent?.name}
                      originalValue={initialRepresentative?.agent?.name}
                    />
                    <Field
                      leftLabel="Full name"
                      value={representative?.agent?.agency}
                      originalValue={initialRepresentative?.agent?.agency}
                    />
                    <Field
                      leftLabel="Email address"
                      value={representative?.agent?.email}
                      originalValue={initialRepresentative?.agent?.email}
                    />
                    <Field
                      leftLabel="Authorised to act"
                      value={representative?.consent}
                      originalValue={initialRepresentative?.consent}
                    />
                  </CustomCollapse.Content>
                </CustomCollapse>
              )}

              {showHelpedByFamilyMember && (
                <CustomCollapse defaultIsOpen>
                  <CustomCollapse.Trigger>{`Family representation`}</CustomCollapse.Trigger>
                  <CustomCollapse.Content>
                    <Field
                      leftLabel="Full name"
                      value={representative?.familyMemberName}
                      originalValue={initialRepresentative?.familyMemberName}
                    />
                    <Field
                      leftLabel="Authorised to act"
                      value={representative?.consent}
                      originalValue={initialRepresentative?.consent}
                    />
                  </CustomCollapse.Content>
                </CustomCollapse>
              )}

              {showHelpedByFriend && (
                <CustomCollapse defaultIsOpen>
                  <CustomCollapse.Trigger>{`Friend representation`}</CustomCollapse.Trigger>
                  <CustomCollapse.Content>
                    <Field
                      leftLabel="Full name"
                      value={representative?.friendName}
                      originalValue={initialRepresentative?.friendName}
                    />
                    <Field
                      leftLabel="Authorised to act"
                      value={representative?.consent}
                      originalValue={initialRepresentative?.consent}
                    />
                  </CustomCollapse.Content>
                </CustomCollapse>
              )}

              {showHelpedBySchoolCounsellor && (
                <CustomCollapse defaultIsOpen>
                  <CustomCollapse.Trigger>{`School counsellor representation`}</CustomCollapse.Trigger>
                  <CustomCollapse.Content>
                    <Field
                      leftLabel="Full name"
                      value={representative?.schoolCounsellorName}
                      originalValue={initialRepresentative?.schoolCounsellorName}
                    />
                    <Field
                      leftLabel="Authorised to act"
                      value={representative?.consent}
                      originalValue={initialRepresentative?.consent}
                    />
                  </CustomCollapse.Content>
                </CustomCollapse>
              )}

              {showHelpedByTeacher && (
                <CustomCollapse defaultIsOpen>
                  <CustomCollapse.Trigger>{`Teacher representation`}</CustomCollapse.Trigger>
                  <CustomCollapse.Content>
                    <Field
                      leftLabel="Full name"
                      value={representative?.teacherName}
                      originalValue={initialRepresentative?.teacherName}
                    />
                    <Field
                      leftLabel="Authorised to act"
                      value={representative?.consent}
                      originalValue={initialRepresentative?.consent}
                    />
                  </CustomCollapse.Content>
                </CustomCollapse>
              )}

              {showHelpedByStudent && (
                <CustomCollapse defaultIsOpen>
                  <CustomCollapse.Trigger>{`Student representation`}</CustomCollapse.Trigger>
                  <CustomCollapse.Content>
                    <Field
                      leftLabel="Full name"
                      value={representative?.studentName}
                      originalValue={initialRepresentative?.studentName}
                    />
                    <Field
                      leftLabel="Authorised to act"
                      value={representative?.consent}
                      originalValue={initialRepresentative?.consent}
                    />
                  </CustomCollapse.Content>
                </CustomCollapse>
              )}

              {showHelpedByOther && (
                <CustomCollapse defaultIsOpen>
                  <CustomCollapse.Trigger>{`Other representation`}</CustomCollapse.Trigger>
                  <CustomCollapse.Content>
                    <Field
                      leftLabel="Full name"
                      value={representative?.otherPresentative?.name}
                      originalValue={initialRepresentative?.otherPresentative?.name}
                    />
                    <Field
                      leftLabel="Relationship"
                      value={representative?.otherPresentative?.relation}
                      originalValue={initialRepresentative?.otherPresentative?.relation}
                    />
                    <Field
                      leftLabel="Authorised to act"
                      value={representative?.consent}
                      originalValue={initialRepresentative?.consent}
                    />
                  </CustomCollapse.Content>
                </CustomCollapse>
              )}
            </Box>
          </>
        )}

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
